import type NumberUnit from '@exodus/currency';
import type { Quote } from '@exodus/fiat-ramp/api';
import Button from '@passkeys/ui/button';
import Text from '@passkeys/ui/text';
import TextField from '@passkeys/ui/text-field';
import { useEffect, useMemo, useState } from 'react';

import type { Asset } from '@/types';
import useConversion from '@/ui/hooks/use-conversion.js';
import { cn } from '@/ui/utils/classnames.js';
import { formatFiat } from '@/ui/utils/formatting';

type CryptoFormProps = {
  fiatAsset: Asset;
  cryptoAsset: Asset;
  fiatAmount: NumberUnit;
  cryptoAmount: NumberUnit;
  quote?: Quote;
  loading?: boolean;
  onAmountChange: (value: NumberUnit) => void;
};

function CryptoForm({
  fiatAsset,
  cryptoAsset,
  fiatAmount,
  cryptoAmount,
  quote,
  loading,
  onAmountChange,
}: CryptoFormProps) {
  const [value, setValue] = useState(cryptoAmount.toDefaultString());

  const convert = useConversion(cryptoAsset.name);

  const minCryptoAmount = useMemo(
    () =>
      convert(fiatAsset.currency.defaultUnit(quote?.from.minBuyAmount ?? 0)),
    [convert, fiatAsset.currency, quote?.from.minBuyAmount],
  );

  useEffect(() => {
    if (!value) {
      return;
    }

    const parsedNumber = Number(value);
    if (!parsedNumber) {
      return;
    }

    const crypto = cryptoAsset.currency.defaultUnit(parsedNumber);
    const fiat = convert(crypto).round();

    onAmountChange(fiat);
  }, [convert, cryptoAsset.currency, onAmountChange, value]);

  return (
    <div className="flex flex-col gap-y-2">
      <TextField
        className={cn(
          // TODO: Clean up so that the consumer doesn't have to know about `after`.
          'bg-white after:bg-white theme-dark:bg-neutral-600 theme-dark:after:bg-neutral-600',
        )}
        value={value}
        placeholder={`${cryptoAsset.displayName} amount`}
        inputMode="decimal"
        suffix={
          <Button
            size="small"
            onClick={() => setValue(minCryptoAmount.toDefaultString())}
            className="uppercase"
            dataTestId="buy-crypto-min-button"
            disabled={loading}
          >
            Min
          </Button>
        }
        onChange={setValue}
        noShadow
      />

      <div className="flex justify-between">
        <Text
          monospace
          size={13}
          className="text-gray-500 theme-dark:text-neutral-400"
        >
          Estimated total
        </Text>
        <Text
          monospace
          size={13}
          className="text-gray-500 theme-dark:text-neutral-400"
        >
          {' '}
          {formatFiat(value ? fiatAmount : 0)}
        </Text>
      </div>
    </div>
  );
}

export default CryptoForm;
